<template>
  <div class="member-trend">
    <div class="module-header">
      <h3 class="module-title">会员活跃趋势</h3>
    </div>
    <div class="chart-container">
      <div ref="chartRef" class="chart"></div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import * as echarts from 'echarts'

export default {
  name: 'MemberTrend',
  data() {
    return {
      chart: null
    }
  },
  computed: {
    ...mapState('member', ['memberTrend'])
  },
  mounted() {
    this.initChart()
    this.updateChart()
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
    }
  },
  watch: {
    'memberTrend.values': {
      handler() {
        if (this.memberTrend.values && Array.isArray(this.memberTrend.values)) {
          this.updateChart()
        }
      },
      deep: true
    }
  },
  methods: {
    initChart() {
      this.chart = echarts.init(this.$refs.chartRef)
      
      const resizeHandler = () => {
        if (this.chart) {
          this.chart.resize()
        }
      }
      
      window.addEventListener('resize', resizeHandler)
      
      // 清理事件监听器
      this.$once('hook:beforeDestroy', () => {
        window.removeEventListener('resize', resizeHandler)
      })
    },
    updateChart() {
      if (!this.chart || !this.memberTrend.values || !Array.isArray(this.memberTrend.values) || !this.memberTrend.values.length) return

      const option = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderColor: '#1890ff',
          textStyle: {
            color: '#ffffff'
          },
          formatter: function(params) {
            return `${params[0].name}<br/>活跃会员: ${params[0].value}人`
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: this.memberTrend.dates,
          axisLabel: {
            color: '#8c8c8c',
            fontSize: 10
          },
          axisLine: {
            lineStyle: {
              color: 'rgba(24, 144, 255, 0.3)'
            }
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            color: '#8c8c8c',
            fontSize: 10,
            formatter: function(value) {
              if (value >= 1000) {
                return (value / 1000).toFixed(1) + 'k'
              }
              return value
            }
          },
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(24, 144, 255, 0.1)'
            }
          }
        },
        series: [
          {
            name: '活跃会员',
            type: 'line',
            smooth: true,
            data: this.memberTrend.values,
            lineStyle: {
              color: '#1890ff',
              width: 3
            },
            itemStyle: {
              color: '#1890ff',
              borderColor: '#ffffff',
              borderWidth: 2
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(24, 144, 255, 0.3)' },
                { offset: 1, color: 'rgba(24, 144, 255, 0.05)' }
              ])
            },
            emphasis: {
              itemStyle: {
                color: '#40a9ff',
                borderColor: '#ffffff',
                borderWidth: 3,
                shadowBlur: 10,
                shadowColor: 'rgba(24, 144, 255, 0.5)'
              }
            }
          }
        ]
      }

      this.chart.setOption(option)
    }
  }
}
</script>

<style lang="scss" scoped>
.member-trend {
  height: 100%;
  display: flex;
  flex-direction: column;

  .module-header {
    margin-bottom: var(--spacing-base);

    .module-title {
      font-size: var(--font-size-lg);
      font-weight: bold;
      color: var(--text-accent);
      text-align: center;
      position: relative;

      &::after {
        content: '';
        position: absolute;
        bottom: -5px;
        left: 50%;
        transform: translateX(-50%);
        width: 60px;
        height: 2px;
        background: linear-gradient(90deg, transparent, var(--primary-color), transparent);
      }
    }
  }

  .chart-container {
    flex: 1;
    position: relative;
    min-height: 200px;

    .chart {
      width: 100%;
      height: 100%;
    }
  }
}

// 响应式设计 - 中等屏幕 (iPad)
@media (max-width: 1440px) and (min-width: 769px) {
  .member-trend {
    .module-header {
      margin-bottom: var(--spacing-sm);

      .module-title {
        font-size: var(--font-size-base);

        &::after {
          width: 50px;
        }
      }
    }

    .chart-container {
      min-height: 180px;
    }
  }
}

// 响应式设计 - 小屏幕 (移动端)
@media (max-width: 768px) {
  .member-trend {
    .module-header {
      margin-bottom: var(--spacing-xs);

      .module-title {
        font-size: var(--font-size-sm);

        &::after {
          width: 40px;
          height: 1px;
        }
      }
    }

    .chart-container {
      min-height: 200px;
    }
  }
}

// 超小屏幕优化
@media (max-width: 480px) {
  .member-trend {
    .chart-container {
      min-height: 180px;
    }
  }
}
</style>